// frontend/src/components/reports/ReportKpiStrip.jsx
// Horizontal KPI summary strip displayed above a report grid.
//
// The header — title, collapse chevron, collapsed digest, persisted collapsed
// state — is Tile's. The strip owns only the cards: the loading spinner beside
// the title, the highlight/active/clickable card states, and the footnote.
//
// Usage:
//   <ReportKpiStrip
//     title={t('reports.x.kpi.title')}
//     items={[
//       { label: 'Items at Risk', value: 12, key: 'at_risk', tooltip: 'Hover text' },
//       { label: 'Total Value', value: '€34,200', highlight: 'error' },
//       { label: 'On Track', value: 85, suffix: '%', highlight: 'success' },
//     ]}
//     loading={loading}
//     activeKey={kpiFilter}
//     onCardClick={handleKpiClick}
//     storageKey="x"
//   />

import React from 'react';
import { ICON, SpinnerGap } from '../../icons';
import InfoHint from '../common/InfoHint';
import Tile from '../layout/Tile';
import './ReportKpiStrip.css';

// One rule for how a KPI reads as text, on the card and in the collapsed digest.
const displayValue = (item) => (item.value != null ? `${item.value}${item.suffix || ''}` : '—');

function ReportKpiStrip({ items = [], title, loading, activeKey, onCardClick, storageKey, footnote }) {
  if (!items.length && !loading) return null;

  // While loading, the digest would show stale numbers next to a spinner, so
  // it is withheld until the cards themselves are current.
  const collapsedItems = loading
    ? undefined
    : items.map((item) => ({ label: item.label, value: displayValue(item) }));

  return (
    <Tile
      title={loading ? (
        <>{title}<SpinnerGap size={ICON.md} className="report-kpi-spinner" /></>
      ) : title}
      className="tile-flush"
      collapsible
      storageKey={storageKey}
      collapsedItems={collapsedItems}
    >
      <div className="report-kpi-strip">
        {items.map((item, idx) => (
          <div
            key={item.key || idx}
            className={`report-kpi-card${item.highlight ? ` report-kpi-${item.highlight}` : ''}${activeKey && activeKey === item.key ? ' report-kpi-active' : ''}${onCardClick ? ' report-kpi-clickable' : ''}`}
            onClick={onCardClick && item.key ? () => onCardClick(item.key) : undefined}
          >
            {item.tooltip && (
              <InfoHint text={item.tooltip} className="report-kpi-card-info" />
            )}
            <span className="report-kpi-value">{displayValue(item)}</span>
            <span className="report-kpi-label">{item.label}</span>
          </div>
        ))}
      </div>
      {/* Report-level disclosure line — used for FX "Valuation as of" labels.
          Renders only when supplied; null/falsy values produce no DOM. */}
      {footnote && <div className="report-kpi-footnote">{footnote}</div>}
    </Tile>
  );
}

export default ReportKpiStrip;
